import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import Card, { ActionButton } from '@/pages/MLProject/components/Card';
import { MediaResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

import VideoVolumeField from './VideoVolumeField';

interface Props {
  videoVolume?: number;
  videoMedia: MediaResource;
  translationId: number;
}

const Video = styled.video`
  width: 100%;
  height: auto;
  max-height: 500px;
  border-radius: 4px;
  margin: 8px 0 24px;
  background: #ececec;

  &::-webkit-media-controls-panel {
    background-image: linear-gradient(transparent, transparent) !important;
  }
`;

const Form = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

export interface FormDataT {
  settings: {
    videoVolume: number;
  };
}

const OriginalVideoCard: FC<Props> = ({
  videoVolume,
  videoMedia,
  translationId,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const alerts = useAlerts();
  const toasts = useToasts();
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting, isDirty },
    setError,
  } = useForm<FormDataT>({
    defaultValues: {
      settings: {
        videoVolume: videoVolume ?? 1,
      },
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    const toastId = toasts.create('loading', 'Saving...');
    try {
      await api.put(`/v1/distribution/ml-translations/${translationId}`, data);

      await queryClient.invalidateQueries([
        'ml_translations',
        { id: translationId.toString() },
      ]);

      reset(data);

      toasts.success(toastId, 'Saved!');
    } catch (err) {
      handleFormErrors(err, setError, alerts);
      toasts.error(toastId, 'Error!');
    }
  });

  const volume = watch('settings.videoVolume');

  useEffect(() => {
    if (!videoRef.current) return;

    videoRef.current.volume = volume;
  }, [volume]);

  return (
    <Card
      storageKey={`ml_translation_${translationId}_original_video_card_visibility`}
      title="Original video & sound volume"
      defaultIsOpened
      description="Review original video and adjust video sound volume for final result"
    >
      <Text $color="gray" $type="smallHeadline">
        Video
      </Text>
      <Video controls key={videoMedia.id} ref={videoRef}>
        <source src={videoMedia.versions.original} type="video/mp4" />
        Your browser does not support the video tag.
      </Video>
      <Form>
        <VideoVolumeField control={control} />
        <ActionButton disabled={isSubmitting || !isDirty} onClick={onSubmit}>
          Save
        </ActionButton>
      </Form>
    </Card>
  );
};

export default OriginalVideoCard;
